import { useMemo, useState } from "react";
import Modal from "../../Common/Modal";
import BankDetailsInput from "./partials";
import { useSelector } from "react-redux";
import CopyToClipboard from "../../../helper/CopyToClipboard";
import CommonButton from "../../Buttons/CommonButton";
import Accordion from "../../Accordion";

const BankDetailsModal = ({ children }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [copiedText, setCopiedText] = useState("");
  const [isCopied, setIsCopied] = useState(false);
  const [message, setIsMessage] = useState("");

  const { activeAccount } = useSelector((state) => state.activeAccount);
  const closeModal = () => setIsModalOpen(false);
  const isValidValue = (value) => value !== null && value !== undefined && value !== "" && value !== "N/A";
  // const routing = {};

  const handleCopy = (text, field) => {
    if (!isValidValue(text)) return;
    CopyToClipboard(
      text,
      setCopiedText,
      setIsCopied,
      setIsMessage,
      `${field} copied`,
    );
  };

  const virtualData = useMemo(() => {
    if (!activeAccount?.virtual_account_info) return null;
    try {
      const parsed =
        typeof activeAccount.virtual_account_info === "string"
          ? JSON.parse(activeAccount.virtual_account_info)
          : activeAccount.virtual_account_info;

      // Return null if it's an empty object {}
      if (Object.keys(parsed).length === 0) return null;
      return parsed;
    } catch (e) {
      return null;
    }
  }, [activeAccount]);

  /**
   * Derive routing details safely
   */
  const routingDetails = useMemo(() => {
    if (!activeAccount?.routing_code_entries) return null;

    try {
      const entries =
        typeof activeAccount.routing_code_entries === "string"
          ? JSON.parse(activeAccount.routing_code_entries)
          : activeAccount.routing_code_entries;

      const entry = entries?.[0];
      if (!entry?.routingCodeValue) return null;

      return {
        key: entry.routingCodeKey,
        label:
          entry.routingCodeKey === "ROUTING_NUMBER"
            ? "Routing number"
            : "Sort code",
        value: entry.routingCodeValue,
      };
    } catch {
      return null;
    }
  }, [activeAccount]);

  /**
   * Reusable input renderer
   */
  const renderField = (label, value, name) => isValidValue(value) && (
      <BankDetailsInput
        label={label}
        value={value}
        name={name}
        onCopy={() => handleCopy(value, label)}
        isCopied={copiedText === value && isCopied}
      />
    );
    
    
  const renderAccountInfo = (accountInfo) => {
    // =========================================
    // NEW ZENUS STRUCTURE
    // =========================================
    if (accountInfo?.accounts && Array.isArray(accountInfo.accounts)) {
      return (
        <>
          {/* Accounts */}
          <div className="pt-2 space-y-3">
            {accountInfo.accounts.map((account, index) => (
              <Accordion
                key={index}
                defaultOpen={false}
                title={account?.title || `Account ${index + 1}`}
              >
                {/* =========================================
                    SWIFT SECTIONS
                ========================================= */}
                {Array.isArray(account?.sections) &&
                  account.sections.map((section, sectionIndex) => (
                    <div
                      key={sectionIndex}
                      className="space-y-3 mb-5"
                    >
                      <h3 className="text-sm font-semibold text-text-primary">
                        {section.title}
                      </h3>

                      <div className="space-y-3">
                        {Object.entries(section.fields || {}).map(
                          ([label, value]) => renderField(label, value, `${account.type}-${section.title}-${label}`)
                        )}
                      </div>
                    </div>
                  ))}

                {/* =========================================
                    NORMAL FIELDS (ACH/FEDWIRE)
                ========================================= */}
                {!account?.sections && (
                  <div className="space-y-3">
                    {Object.entries(account?.fields || {}).map(
                      ([label, value]) => renderField(label, value, `${account.type}-${label}`)
                    )}
                  </div>
                )}

                {/* =========================================
                    COPY BUTTON
                ========================================= */}
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => handleCopyAccountDetails(account)}
                    className="px-4 py-2 text-xs font-medium rounded-xl border border-divider bg-background-secondary hover:bg-background-muted transition-all duration-200"
                  >
                    Copy {account?.title || account?.type} Details
                  </button>
                </div>
              </Accordion>
            ))}
          </div>
        </>
      );
    }

    // =========================================
    // OLD STRUCTURE FALLBACK
    // =========================================
    if (!Array.isArray(accountInfo) || !accountInfo.length) {
      return null;
    }

    const baseAccount = accountInfo[0];

    const routing = accountInfo.reduce((acc, item) => {
      const type = item?.financialInstitutionId?.type;
      const value = item?.financialInstitutionId?.value;
      const isVRN = item?.accountNumberSubtype === "VRN";

      if (isVRN && item?.accountNumber?.value) {
        acc.VRN = item.accountNumber.value;
      }

      if (type && value) {
        acc[type] = value;
      }

      return acc;
    }, {});

    return (
      <>
        {renderField("Beneficiary name", activeAccount?.bank_account_holder_name, "beneficiaryname")}
        {renderField("Bank name", activeAccount?.bank_name, "bankName")}
        {renderField("Account number", activeAccount?.virtual_account_number, "virtualAccountNumber")}
        {renderField("IBAN", activeAccount?.iban, "iban")}
        {routingDetails && renderField(routingDetails.label, routingDetails.value, routingDetails.key)}
        {renderField("Bank Address", activeAccount?.bank_address, "bankAddress")}
        {routing?.ABA_CODE && renderField("Routing Code", routing.ABA_CODE, "routingCode")}
        {routing?.BIC && renderField("BIC/Swift Code", routing.BIC, "bic")}
        {routing?.VRN && renderField("VRN", routing.VRN, "vrn")}
        {renderField("Currency", baseAccount?.defaultCurrencyCode, "currency")}
      </>
    );
  };

  /**
   * Copy all visible details
   */
  const handleCopyAll = () => {
    if (!activeAccount) return;

    const details = [];

    // =========================================
    // ZENUS HANDLING
    // =========================================
    if (activeAccount?.virtual_provider === "zenus" && activeAccount?.virtual_account_info?.accounts) {
      const virtualInfo = activeAccount.virtual_account_info;

      // Accounts
      virtualInfo.accounts.forEach((account) => {
        details.push(
          `\n================================`,
          `${account.title || account.type}`,
          `================================`
        );

        // =====================================
        // SWIFT SECTIONS
        // =====================================
        if (Array.isArray(account.sections)) {
          account.sections.forEach((section) => {
            details.push(`\n${section.title}`);

            Object.entries(section.fields || {}).forEach(
              ([label, value]) => {
                if (isValidValue(value)) {
                  details.push(`${label}: ${value}`);
                }
              }
            );
          });
        }

        // =====================================
        // ACH / FEDWIRE
        // =====================================
        else if (account.fields) {
          Object.entries(account.fields).forEach(
            ([label, value]) => {
              if (isValidValue(value)) {
                details.push(`${label}: ${value}`);
              }
            }
          );
        }
      });
    }

    // =========================================
    // LORUM HANDLING
    // =========================================
    else if (activeAccount?.virtual_provider === "lorum" && activeAccount?.virtual_account_info) {
      const info = activeAccount.virtual_account_info;

      details.push(
        `================================`,
        `${activeAccount.title || "Account Details"}`,
        `================================`
      );

      // General bank info (top-level)
      const generalFields = [
        ["Beneficiary Name", info.beneficiary_name],
        ["Bank Name", info.bank_name],
        ["Currency", info.currency],
        ["Swift Code", info.swift_code],
        ["Bank Address", info.bank_address],
        ["Account Opened Date", info.account_opened_date],
        ["Beneficiary Address", info.beneficiary_address],
      ];

      generalFields.forEach(([label, value]) => {
        if (isValidValue(value)) {
          details.push(`${label}: ${value}`);
        }
      });

      // Accounts (SWIFT / FEDWIRE / ACH etc)
      if (Array.isArray(info.accounts)) {
        info.accounts.forEach((account) => {
          details.push(
            `\n--------------------------------`,
            `${account.type || "Account"}`,
            `--------------------------------`
          );

          const accountFields = [
            ["Account Number", account.account_number],
            ["IBAN", account.iban],
            ["Routing Number", account.routing_number],
          ];

          accountFields.forEach(([label, value]) => {
            if (isValidValue(value)) {
              details.push(`${label}: ${value}`);
            }
          });
        });
      }
    }


    // =========================================
    // NORMAL SEPA
    // =========================================
    else if (activeAccount?.is_sepa) {
      [
        ["Beneficiary name", activeAccount.bank_account_holder_name],
        ["Bank name", activeAccount.bank_name],
        ["Account number", activeAccount.virtual_account_number],
        ["IBAN", activeAccount.iban],
        ["BIC", activeAccount.bic],
        [routingDetails?.label, routingDetails?.value],
        ["Currency", activeAccount.currency_code],
        ["Bank Address", activeAccount.bank_address],
      ]
        .filter(([, value]) => isValidValue(value))
        .forEach(([label, value]) => {
          details.push(`${label}: ${value}`);
        });
    }

    // =========================================
    // BASIC ACCOUNT
    // =========================================
    else {
      [
        ["Beneficiary name", activeAccount.title],
        ["Account number", activeAccount.account_number],
      ]
        .filter(([, value]) => isValidValue(value))
        .forEach(([label, value]) => {
          details.push(`${label}: ${value}`);
        });
    }

    // =========================================
    // COPY
    // =========================================
    CopyToClipboard(
      details.join("\n").trim(),
      setCopiedText,
      setIsCopied,
      setIsMessage,
      "Bank details copied"
    );
  };


  // =========================================
  // COPY SINGLE ACCOUNT DETAILS
  // =========================================
  const handleCopyAccountDetails = (account) => {
    if (!account) return;

    const details = [];

    // =====================================
    // TITLE
    // =====================================
    details.push(
      "================================",
      `${account.title || account.type}`,
      "================================"
    );

    // =====================================
    // SWIFT STRUCTURE
    // =====================================
    if (Array.isArray(account?.sections)) {
      account.sections.forEach((section) => {
        details.push(`\n${section.title}`);

        Object.entries(section.fields || {}).forEach(
          ([label, value]) => {
            if (isValidValue(value)) {
              details.push(`${label}: ${value}`);
            }
          }
        );
      });
    }

    // =====================================
    // ACH / FEDWIRE
    // =====================================
    else if (account?.fields) {
      Object.entries(account.fields).forEach(
        ([label, value]) => {
          if (isValidValue(value)) {
            details.push(`${label}: ${value}`);
          }
        }
      );
    }

    // =====================================
    // COPY
    // =====================================
    CopyToClipboard(
      details.join("\n").trim(),
      setCopiedText,
      setIsCopied,
      setIsMessage,
      `${account.title || account.type} details copied`
    );
  };

  return (
    <>
      <div onClick={() => setIsModalOpen(true)}>{children}</div>

      <Modal
        isOpen={isModalOpen}
        handleOnCloseModal={closeModal}
        header
        headerTitle="Bank Details"
        className="w-full max-w-[432px] py-4 overflow-scroll hide-scrollbar shadow-xl rounded-3xl mx-auto"
      >
        <div className="flex flex-col md:px-6 px-4 py-3 space-y-4">
          {virtualData ? (
            /* TIER 1: Virtual Account Info exists */
            <>
              {
                activeAccount.virtual_provider === "zenus" ?
                (
                  <div>
                    {activeAccount?.is_sepa && renderAccountInfo(virtualData)}
                  </div>
                ) : (
                <div className="pt-2 space-y-3">
                  {renderField("Beneficiary name", virtualData.beneficiary_name, "beneficiaryname")}
                  {renderField("Beneficiary address", virtualData.beneficiary_address, "beneficiaryaddress")}
                  {renderField("Bank Address", virtualData.bank_address, "bankAddress")}
                  {renderField("Bank name", virtualData.bank_name, "bankName")}
                  {renderField("Currency", virtualData.currency, "currency")}
                  {renderField("Swift code", virtualData.swift_code, "swiftCode")}
                  
                  {virtualData.accounts?.map((acc, index) => {
                    const accordionTitle = acc.type
                      ? `${acc.type}`
                      : acc.iban
                        ? "IBAN"
                        : `Account ${index + 1}`;

                    return (
                      <Accordion
                        key={index}
                        title={accordionTitle}
                        contentClassName="items-center"
                      >
                        {renderField("Account number", acc.account_number)}
                        {renderField("Routing number", acc.routing_number)}
                        {renderField("IBAN", acc.iban)}
                      </Accordion>
                    );
                  })}
                </div>
                )
              }
              
            </>
          ) : activeAccount?.is_sepa ? (
            /* TIER 2: SEPA Account without Virtual Info */
            <>
                {renderField("Beneficiary name", activeAccount.bank_account_holder_name, "beneficiaryname")}
                {renderField("Bank name", activeAccount.bank_name, "bankName")}
                {renderField("Account number", activeAccount.virtual_account_number, "virtualAccountNumber")}
                {renderField("IBAN", activeAccount.iban, "iban")}
                {renderField("BIC code", activeAccount.bic, "bic")}

                {routingDetails && renderField(routingDetails.label, routingDetails.value, routingDetails.key)}

                {renderField("Currency code", activeAccount.currency_code, "currency")}
                {renderField("Bank Address", activeAccount.bank_address, "bankAddress")}
              </>
          ) : (
            /* TIER 3: Basic Account Info */
            <>
              {renderField("Beneficiary name", activeAccount?.title, "beneficiaryname")}
              {renderField("Account number", activeAccount?.account_number, "accountNumber")}
            </>
          )}
        </div>

        <div className="flex flex-col items-center px-4 mt-6">
          <CommonButton onClick={handleCopyAll}>Copy all details</CommonButton>
        </div>

        {message && (
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 px-4 py-2 rounded-lg bg-message-box-bg shadow-md">
            <p className="text-white text-sm">{message}</p>
          </div>
        )}
      </Modal>
    </>
  );
};

export default BankDetailsModal;
